import { useEffect, useRef, useState } from "react";
import {
  Menu, X, ShieldCheck, Gem, Stethoscope, ScanSearch, HeartHandshake,
  Smile, Utensils, Camera, Activity, ChevronDown, MessageCircle,
  Instagram, Facebook, MapPin, Clock, Phone, Star, ArrowRight,
  CheckCircle2, Microscope, Layers, Crown, Wrench, Compass,
} from "lucide-react";

import drHero from "@/assets/dr-lucas-hero.jpg";
import drAbout from "@/assets/dr-lucas-about.jpg";
import result1 from "@/assets/result-1.jpg";
import result2 from "@/assets/result-2.jpg";
import result3 from "@/assets/result-3.jpg";
import result4 from "@/assets/result-4.jpg";



/* ───────────────────────── Scroll reveal hook ───────────────────────── */
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("reveal-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );
    el.querySelectorAll<HTMLElement>("[data-reveal]").forEach((node) => {
      node.classList.add("reveal");
      io.observe(node);
    });
    return () => io.disconnect();
  }, []);
  return ref;
}

/* ───────────────────────── Animated counter ─────────────────────────── */
function Counter({ to, suffix = "" }: { to: number; suffix?: string }) {
  const [n, setN] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const start = performance.now();
          const dur = 1800;
          const step = (t: number) => {
            const p = Math.min(1, (t - start) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            setN(Math.round(eased * to));
            if (p < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
          io.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [to]);
  return (
    <span ref={ref}>
      {n}
      {suffix}
    </span>
  );
}

/* ───────────────────────── Header ───────────────────────── */
function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const nav = [
    { href: "#inicio", label: "Início" },
    { href: "#sobre", label: "Sobre" },
    { href: "#tratamentos", label: "Tratamentos" },
    { href: "#resultados", label: "Resultados" },
    { href: "#depoimentos", label: "Depoimentos" },
    { href: "#contato", label: "Contato" },
  ];

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "glass shadow-[var(--shadow-soft)]" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <a href="#inicio" className="group flex items-center gap-2">
          <span
            className={`grid h-10 w-10 place-items-center rounded-full font-display text-lg font-bold transition-colors ${
              scrolled ? "bg-navy-deep text-white" : "bg-white/95 text-navy-deep"
            }`}
          >
            LF
          </span>
          <div className="leading-tight">
            <div
              className={`font-display text-base font-semibold tracking-tight ${
                scrolled ? "text-navy-deep" : "text-white"
              }`}
            >
              Dr. Lucas Ferreira
            </div>
            <div
              className={`text-[10px] uppercase tracking-[0.2em] ${
                scrolled ? "text-muted-foreground" : "text-white/75"
              }`}
            >
              Implantodontia
            </div>
          </div>
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {nav.map((i) => (
            <a
              key={i.href}
              href={i.href}
              className={`relative text-sm font-medium transition-colors after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-azure after:transition-all hover:after:w-full ${
                scrolled ? "text-navy-deep hover:text-azure" : "text-white/85 hover:text-white"
              }`}
            >
              {i.label}
            </a>
          ))}
        </nav>

        <a
          href="#contato"
          className="hidden items-center gap-2 rounded-full bg-azure px-5 py-2.5 text-sm font-semibold text-white shadow-[var(--shadow-soft)] transition-all hover:bg-navy-deep hover:shadow-[var(--shadow-elegant)] lg:inline-flex"
        >
          Agendar Avaliação
          <ArrowRight className="h-4 w-4" />
        </a>

        <button
          onClick={() => setOpen((v) => !v)}
          aria-label="Abrir menu"
          className={`lg:hidden ${scrolled ? "text-navy-deep" : "text-white"}`}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="glass border-t border-white/40 lg:hidden">
          <nav className="flex flex-col px-6 py-4">
            {nav.map((i) => (
              <a
                key={i.href}
                href={i.href}
                onClick={() => setOpen(false)}
                className="py-3 text-sm font-medium text-navy-deep"
              >
                {i.label}
              </a>
            ))}
            <a
              href="#contato"
              className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-azure px-5 py-3 text-sm font-semibold text-white"
            >
              Agendar Avaliação
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}

/* ───────────────────────── Hero ───────────────────────── */
function Hero() {
  return (
    <section
      id="inicio"
      className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28"
      style={{ background: "var(--gradient-hero)" }}
    >
      <div className="bg-blueprint absolute inset-0 opacity-[0.08]" aria-hidden />
      <div
        className="absolute -left-32 top-20 h-96 w-96 rounded-full opacity-30 blur-3xl"
        style={{ background: "var(--azure)" }}
        aria-hidden
      />
      <div
        className="absolute -right-40 bottom-0 h-[28rem] w-[28rem] rounded-full opacity-25 blur-3xl"
        style={{ background: "var(--azure-light)" }}
        aria-hidden
      />

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2 lg:px-10">
        <div className="fade-up text-white">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.18em] text-white/90 backdrop-blur">
            <Gem className="h-3.5 w-3.5 text-azure-light" />
            Implantodontia · Reabilitação Oral
          </div>

          <h1 className="font-display text-[2.6rem] leading-[1.05] font-semibold sm:text-5xl lg:text-[3.75rem]">
            Volte a sorrir <br />
            <span className="italic text-azure-light">com segurança</span> <br />
            e confiança.
          </h1>

          <p className="mt-7 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg">
            Especialista em implantes dentários e reabilitação oral. Tratamentos modernos
            para devolver funcionalidade, estética e qualidade de vida.
          </p>

          <div className="mt-9 flex flex-wrap gap-4">
            <a
              href="#contato"
              className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-navy-deep shadow-[var(--shadow-elegant)] transition-all hover:-translate-y-0.5 hover:bg-azure-light hover:text-navy-deep"
            >
              <MessageCircle className="h-4 w-4" />
              Agendar Avaliação
            </a>
            <a
              href="#tratamentos"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 text-sm font-semibold text-white transition-all hover:bg-white/10"
            >
              Conhecer Tratamentos
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          <div className="mt-10 inline-flex items-center gap-3 rounded-2xl border border-white/15 bg-white/[0.06] px-5 py-3 backdrop-blur">
            <div className="grid h-10 w-10 place-items-center rounded-full bg-azure/25 text-azure-light">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold text-white">
                Mais de <Counter to={12} />+ anos de experiência
              </div>
              <div className="text-xs text-white/70">CRO ativo · Centenas de sorrisos restaurados</div>
            </div>
          </div>
        </div>

        <div className="relative">
          <div
            className="absolute inset-0 -z-10 mx-auto h-[110%] w-[110%] rounded-[3rem] blur-3xl opacity-40"
            style={{ background: "radial-gradient(closest-side, var(--azure) 0%, transparent 70%)" }}
            aria-hidden
          />
          <div className="floaty relative mx-auto max-w-md overflow-hidden rounded-[2.2rem] border border-white/20 shadow-[var(--shadow-elegant)]">
            <img
              src={drHero}
              alt="Dr. Lucas Ferreira, implantodontista"
              width={1024}
              height={1280}
              className="aspect-[4/5] w-full object-cover"
            />
            <div className="glass-dark absolute bottom-4 left-4 right-4 flex items-center justify-between rounded-2xl px-4 py-3 text-white">
              <div>
                <div className="font-display text-base">Dr. Lucas Ferreira</div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-white/70">
                  Implantodontista · CRO 00000
                </div>
              </div>
              <CheckCircle2 className="h-6 w-6 text-azure-light" />
            </div>
          </div>
        </div>
      </div>

      {/* stat strip */}
      <div className="relative mx-auto mt-20 grid max-w-6xl grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/15 bg-white/[0.04] px-2 backdrop-blur md:grid-cols-4">
        {[
          { n: 12, s: "+", label: "Anos de experiência" },
          { n: 1500, s: "+", label: "Implantes instalados" },
          { n: 98, s: "%", label: "Pacientes satisfeitos" },
          { n: 24, s: "h", label: "Suporte pós-cirúrgico" },
        ].map((s) => (
          <div key={s.label} className="px-6 py-6 text-center text-white">
            <div className="font-display text-3xl font-semibold text-azure-light sm:text-4xl">
              <Counter to={s.n} suffix={s.s} />
            </div>
            <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-white/70">
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ───────────────────────── Problems ───────────────────────── */
function Problems() {
  const ref = useReveal();
  const items = [
    { icon: Smile, t: "Perdeu um ou mais dentes?", d: "Recupere o sorriso completo com implantes que se integram ao seu osso." },
    { icon: Utensils, t: "Tem dificuldade para mastigar?", d: "Volte a saborear seus alimentos favoritos com total funcionalidade." },
    { icon: Camera, t: "Evita sorrir em fotos?", d: "Devolva naturalidade e estética ao seu sorriso." },
    { icon: Activity, t: "Usa prótese móvel desconfortável?", d: "Substitua por próteses fixas sobre implantes — estáveis e seguras." },
  ];
  return (
    <section ref={ref} className="section-y bg-secondary">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="mx-auto max-w-2xl text-center" data-reveal>
          <div className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-azure">
            Diagnóstico
          </div>
          <h2 className="font-display text-4xl font-semibold text-navy-deep sm:text-5xl">
            Você se identifica com alguma dessas situações?
          </h2>
          <p className="mt-4 text-muted-foreground">
            Se reconheceu em pelo menos uma delas, este é o momento certo para uma avaliação especializada.
          </p>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((p, i) => (
            <div
              key={p.t}
              data-reveal
              style={{ transitionDelay: `${i * 90}ms` }}
              className="hover-lift group relative overflow-hidden rounded-2xl border border-border bg-white p-7"
            >
              <div
                className="absolute -right-10 -top-10 h-32 w-32 rounded-full opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                style={{ background: "var(--azure)", filter: "blur(60px)" }}
              />
              <div className="relative">
                <div className="mb-5 grid h-12 w-12 place-items-center rounded-xl bg-navy-deep/5 text-navy-deep transition-colors group-hover:bg-navy-deep group-hover:text-white">
                  <p.icon className="h-6 w-6" />
                </div>
                <h3 className="font-display text-xl font-semibold text-navy-deep">{p.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── About ───────────────────────── */
function About() {
  const ref = useReveal();
  const credentials = [
    { icon: Stethoscope, t: "Implantodontista", d: "Especialização em implantes e reabilitação." },
    { icon: ScanSearch, t: "Planejamento Digital", d: "Tomografia e modelagem 3D do sorriso." },
    { icon: Compass, t: "Cirurgia Guiada", d: "Precisão milimétrica em cada implante." },
    { icon: HeartHandshake, t: "Atendimento Humanizado", d: "Cuidado, escuta e acolhimento sempre." },
  ];

  return (
    <section ref={ref} id="sobre" className="section-y">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2 lg:px-10">
        <div data-reveal className="relative">
          <div
            className="absolute -inset-6 -z-10 rounded-[2.5rem] opacity-60"
            style={{ background: "linear-gradient(135deg, var(--azure-light), transparent 60%)" }}
            aria-hidden
          />
          <img
            src={drAbout}
            alt="Dr. Lucas Ferreira durante planejamento digital"
            width={1024}
            height={1280}
            loading="lazy"
            className="aspect-[4/5] w-full rounded-[2rem] object-cover shadow-[var(--shadow-elegant)]"
          />
          <div className="glass absolute -bottom-8 left-6 right-6 rounded-2xl p-5 shadow-[var(--shadow-soft)] sm:left-10 sm:right-auto sm:w-72">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-full bg-azure text-white">
                <Crown className="h-5 w-5" />
              </div>
              <div>
                <div className="font-display text-lg font-semibold text-navy-deep">
                  <Counter to={1500} suffix="+" />
                </div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  implantes realizados
                </div>
              </div>
            </div>
          </div>
        </div>

        <div data-reveal>
          <div className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-azure">
            Sobre o doutor
          </div>
          <h2 className="font-display text-4xl font-semibold text-navy-deep sm:text-5xl">
            Conheça o <span className="italic">Dr. Lucas Ferreira</span>
          </h2>
          <p className="mt-6 leading-relaxed text-muted-foreground">
            O Dr. Lucas Ferreira dedica sua carreira à reabilitação oral e implantodontia,
            ajudando pacientes a recuperarem sua autoestima, conforto e qualidade de vida
            através de tratamentos modernos e personalizados.
          </p>
          <p className="mt-4 leading-relaxed text-muted-foreground">
            Com formação contínua nas mais avançadas técnicas internacionais, combina
            tecnologia, planejamento digital e um atendimento profundamente humano.
          </p>

          <div className="mt-9 grid gap-4 sm:grid-cols-2">
            {credentials.map((c, i) => (
              <div
                key={c.t}
                style={{ transitionDelay: `${i * 80}ms` }}
                data-reveal
                className="hover-lift rounded-xl border border-border bg-white p-5"
              >
                <div className="mb-3 grid h-10 w-10 place-items-center rounded-lg bg-azure/10 text-azure">
                  <c.icon className="h-5 w-5" />
                </div>
                <div className="font-display text-base font-semibold text-navy-deep">{c.t}</div>
                <div className="mt-1 text-xs text-muted-foreground">{c.d}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Treatments ───────────────────────── */
function Treatments() {
  const ref = useReveal();
  const items = [
    { icon: Microscope, t: "Implante Unitário", d: "Reposição estética de um único dente perdido com integração natural." },
    { icon: Layers, t: "Prótese sobre Implantes", d: "Soluções fixas e removíveis ancoradas com estabilidade." },
    { icon: Smile, t: "Reabilitação Oral", d: "Plano completo para devolver função, estética e saúde bucal." },
    { icon: Wrench, t: "Enxerto Ósseo", d: "Reconstrução óssea para receber implantes com segurança." },
    { icon: Crown, t: "Protocolo Fixo", d: "Arcada completa fixa sobre implantes — definitiva e confortável." },
    { icon: Compass, t: "Cirurgia Guiada", d: "Tecnologia 3D para precisão e menor tempo de recuperação." },
  ];
  return (
    <section ref={ref} id="tratamentos" className="section-y bg-secondary">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="mx-auto max-w-2xl text-center" data-reveal>
          <div className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-azure">
            Tratamentos
          </div>
          <h2 className="font-display text-4xl font-semibold text-navy-deep sm:text-5xl">
            Soluções para recuperar seu sorriso
          </h2>
          <p className="mt-4 text-muted-foreground">
            Cada paciente é único. Nosso planejamento é individualizado e baseado em diagnóstico preciso.
          </p>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((t, i) => (
            <article
              key={t.t}
              data-reveal
              style={{ transitionDelay: `${i * 80}ms` }}
              className="hover-lift group relative overflow-hidden rounded-2xl border border-border bg-white p-8"
            >
              <div
                className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 transition-transform duration-500 group-hover:scale-x-100"
                style={{ background: "var(--gradient-azure)" }}
              />
              <div className="mb-6 inline-flex items-center justify-center rounded-2xl bg-navy-deep p-3.5 text-white transition-transform group-hover:scale-110">
                <t.icon className="h-6 w-6" />
              </div>
              <h3 className="font-display text-xl font-semibold text-navy-deep">{t.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t.d}</p>
              <a
                href="#contato"
                className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-azure transition-all group-hover:gap-3"
              >
                Saiba mais <ArrowRight className="h-4 w-4" />
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Results gallery ───────────────────────── */
function Results() {
  const ref = useReveal();
  const [open, setOpen] = useState<number | null>(null);
  const cases = [
    { img: result1, t: "Implante unitário", d: "Reposição de dente anterior com prótese personalizada." },
    { img: result2, t: "Reabilitação completa", d: "Restabelecimento de função e estética." },
    { img: result3, t: "Protocolo fixo", d: "Arcada superior reabilitada com implantes." },
    { img: result4, t: "Prótese sobre implantes", d: "Estabilidade, conforto e autoestima recuperados." },
  ];

  return (
    <section ref={ref} id="resultados" className="section-y">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="mx-auto max-w-2xl text-center" data-reveal>
          <div className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-azure">
            Resultados
          </div>
          <h2 className="font-display text-4xl font-semibold text-navy-deep sm:text-5xl">
            Resultados que transformam vidas
          </h2>
          <p className="mt-4 text-muted-foreground">
            Casos reais — sorrisos reabilitados com técnica, planejamento e cuidado.
          </p>
        </div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {cases.map((c, i) => (
            <button
              key={c.t}
              data-reveal
              onClick={() => setOpen(i)}
              style={{ transitionDelay: `${i * 90}ms` }}
              className="group relative aspect-[3/4] overflow-hidden rounded-2xl text-left"
            >
              <img
                src={c.img}
                alt={c.t}
                width={1024}
                height={1024}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, transparent 35%, oklch(0.18 0.07 258 / 0.85) 100%)",
                }}
              />
              <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">
                  Caso clínico
                </div>
                <div className="mt-1 font-display text-lg">{c.t}</div>
                <div className="mt-3 inline-flex items-center gap-2 text-xs font-medium text-azure-light opacity-0 transition-opacity group-hover:opacity-100">
                  Ver detalhes <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {open !== null && (
        <div
          className="fixed inset-0 z-[80] grid place-items-center bg-navy-deep/85 p-6 backdrop-blur-md"
          onClick={() => setOpen(null)}
        >
          <div
            className="relative max-w-3xl overflow-hidden rounded-3xl bg-white shadow-[var(--shadow-elegant)]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpen(null)}
              className="absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center rounded-full bg-white/90 text-navy-deep"
              aria-label="Fechar"
            >
              <X className="h-4 w-4" />
            </button>
            <img
              src={cases[open].img}
              alt={cases[open].t}
              className="aspect-[4/3] w-full object-cover"
            />
            <div className="p-7">
              <div className="text-xs uppercase tracking-[0.2em] text-azure">Caso clínico</div>
              <h3 className="mt-2 font-display text-2xl text-navy-deep">{cases[open].t}</h3>
              <p className="mt-2 text-muted-foreground">{cases[open].d}</p>
              <a
                href="#contato"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-navy-deep px-5 py-2.5 text-sm font-semibold text-white"
              >
                Quero um caso assim <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* ───────────────────────── Process timeline ───────────────────────── */
function Process() {
  const ref = useReveal();
  const steps = [
    { n: "01", t: "Avaliação Inicial", d: "Diagnóstico clínico e conversa para entender suas expectativas." },
    { n: "02", t: "Exames e Planejamento", d: "Tomografia 3D e planejamento digital personalizado." },
    { n: "03", t: "Instalação dos Implantes", d: "Cirurgia guiada — precisa, segura e minimamente invasiva." },
    { n: "04", t: "Reabilitação Oral", d: "Próteses sob medida que devolvem função e estética." },
    { n: "05", t: "Acompanhamento", d: "Suporte e manutenção contínua para resultados duradouros." },
  ];

  return (
    <section ref={ref} className="section-y bg-secondary">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="mx-auto max-w-2xl text-center" data-reveal>
          <div className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-azure">
            Processo
          </div>
          <h2 className="font-display text-4xl font-semibold text-navy-deep sm:text-5xl">
            Como funciona seu tratamento
          </h2>
        </div>

        <div className="relative mt-16">
          <div
            className="absolute left-1/2 top-0 hidden h-full w-px -translate-x-1/2 lg:block"
            style={{ background: "linear-gradient(180deg, var(--azure) 0%, transparent 100%)" }}
            aria-hidden
          />
          <ol className="space-y-12">
            {steps.map((s, i) => (
              <li
                key={s.n}
                data-reveal
                className={`relative grid items-center gap-6 lg:grid-cols-2 ${
                  i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""
                }`}
              >
                <div className={`lg:${i % 2 ? "pl-16" : "pr-16 text-right"}`}>
                  <div className="font-display text-6xl font-semibold text-azure/30">{s.n}</div>
                  <h3 className="mt-2 font-display text-2xl font-semibold text-navy-deep">
                    {s.t}
                  </h3>
                  <p className="mt-2 text-muted-foreground">{s.d}</p>
                </div>
                <div className="hidden lg:block">
                  <span
                    className="absolute left-1/2 top-8 grid h-12 w-12 -translate-x-1/2 place-items-center rounded-full bg-navy-deep text-white shadow-[var(--shadow-soft)]"
                  >
                    <CheckCircle2 className="h-5 w-5 text-azure-light" />
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Testimonials ───────────────────────── */
const TESTIMONIALS = [
  { n: "Carlos Henrique", t: "Fiquei anos sem sorrir com confiança. Hoje voltei a mastigar normalmente e me sinto outra pessoa." },
  { n: "Paulo Roberto", t: "O atendimento foi excelente desde a primeira consulta. O resultado superou minhas expectativas." },
  { n: "Márcia Oliveira", t: "Profissional extremamente cuidadoso. Me senti segura durante todo o tratamento." },
  { n: "José Antônio", t: "Recuperei minha autoestima e qualidade de vida. Recomendo de olhos fechados." },
  { n: "Fernanda Lima", t: "Equipe acolhedora e doutor muito atencioso. Explicou cada etapa com clareza." },
  { n: "Ricardo Souza", t: "Implantes perfeitos. Mastigo, sorrio e converso sem qualquer desconforto." },
  { n: "Ana Paula", t: "Tinha medo de cirurgia. O Dr. Lucas me deu total segurança — foi tranquilo." },
  { n: "Roberto Mendes", t: "Tratamento sério, com tecnologia de ponta. Vale cada centavo investido." },
  { n: "Sandra Almeida", t: "Voltei a sorrir nas fotos depois de muitos anos. Resultado natural e bonito." },
  { n: "Eduardo Castro", t: "Profissional referência. Planejamento impecável do início ao fim." },
];

function Testimonials() {
  const ref = useReveal();
  const loop = [...TESTIMONIALS, ...TESTIMONIALS];
  return (
    <section ref={ref} id="depoimentos" className="section-y overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="mx-auto max-w-2xl text-center" data-reveal>
          <div className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-azure">
            Depoimentos
          </div>
          <h2 className="font-display text-4xl font-semibold text-navy-deep sm:text-5xl">
            Pacientes que recuperaram a confiança para sorrir
          </h2>
        </div>
      </div>

      <div
        className="relative mt-14"
        style={{
          maskImage:
            "linear-gradient(90deg, transparent, black 8%, black 92%, transparent)",
          WebkitMaskImage:
            "linear-gradient(90deg, transparent, black 8%, black 92%, transparent)",
        }}
      >
        <div className="marquee-track flex w-max gap-6 px-6">
          {loop.map((t, i) => (
            <article
              key={i}
              className="w-[320px] shrink-0 rounded-2xl border border-border bg-white p-7 shadow-[var(--shadow-soft)] sm:w-[360px]"
            >
              <div className="flex items-center gap-3">
                <div className="grid h-12 w-12 place-items-center rounded-full bg-navy-deep font-display text-base text-white">
                  {t.n
                    .split(" ")
                    .map((p) => p[0])
                    .slice(0, 2)
                    .join("")}
                </div>
                <div>
                  <div className="font-semibold text-navy-deep">{t.n}</div>
                  <div className="flex gap-0.5 text-azure">
                    {Array.from({ length: 5 }).map((_, k) => (
                      <Star key={k} className="h-3.5 w-3.5 fill-azure" />
                    ))}
                  </div>
                </div>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">"{t.t}"</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── FAQ ───────────────────────── */
function FAQ() {
  const ref = useReveal();
  const items = [
    { q: "Implante dói?", a: "Não. O procedimento é feito com anestesia local e tecnologia minimamente invasiva. A maioria dos pacientes relata desconforto muito menor do que esperavam." },
    { q: "Quanto tempo leva o tratamento?", a: "Depende do caso. Implantes simples podem ser finalizados em poucos meses; reabilitações completas geralmente entre 4 e 8 meses." },
    { q: "Posso parcelar?", a: "Sim. Oferecemos planos de pagamento flexíveis adaptados ao seu orçamento, discutidos durante a avaliação." },
    { q: "Qualquer pessoa pode fazer implante?", a: "A maioria dos adultos é candidata. Avaliamos saúde geral e qualidade óssea — quando necessário, realizamos enxerto ósseo prévio." },
    { q: "Quanto dura um implante?", a: "Com manutenção adequada, implantes podem durar décadas. Acompanhamento periódico é essencial para preservar o resultado." },
  ];
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section ref={ref} className="section-y bg-secondary">
      <div className="mx-auto max-w-4xl px-6 lg:px-10">
        <div className="text-center" data-reveal>
          <div className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-azure">
            FAQ
          </div>
          <h2 className="font-display text-4xl font-semibold text-navy-deep sm:text-5xl">
            Perguntas Frequentes
          </h2>
        </div>

        <div className="mt-12 space-y-3">
          {items.map((it, i) => {
            const isOpen = open === i;
            return (
              <div
                key={it.q}
                data-reveal
                className="overflow-hidden rounded-2xl border border-border bg-white"
              >
                <button
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                >
                  <span className="font-display text-base font-semibold text-navy-deep sm:text-lg">
                    {it.q}
                  </span>
                  <ChevronDown
                    className={`h-5 w-5 shrink-0 text-azure transition-transform duration-300 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <div
                  className={`grid transition-all duration-500 ease-out ${
                    isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="px-6 pb-6 text-sm leading-relaxed text-muted-foreground">
                      {it.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Final CTA ───────────────────────── */
function FinalCTA() {
  const ref = useReveal();
  return (
    <section
      ref={ref}
      className="relative overflow-hidden section-y"
      style={{ background: "var(--gradient-hero)" }}
    >
      <div className="bg-blueprint absolute inset-0 opacity-[0.08]" aria-hidden />
      <div
        className="absolute -left-32 top-1/2 h-96 w-96 -translate-y-1/2 rounded-full opacity-30 blur-3xl"
        style={{ background: "var(--azure)" }}
        aria-hidden
      />
      <div
        className="absolute -right-40 top-0 h-96 w-96 rounded-full opacity-20 blur-3xl"
        style={{ background: "var(--azure-light)" }}
        aria-hidden
      />

      <div className="relative mx-auto max-w-4xl px-6 text-center lg:px-10" data-reveal>
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.18em] text-white/90 backdrop-blur">
          <Gem className="h-3.5 w-3.5 text-azure-light" />
          Atendimento exclusivo
        </div>
        <h2 className="font-display text-4xl font-semibold text-white sm:text-5xl lg:text-6xl">
          Seu sorriso pode voltar <br />
          a ser <span className="italic text-azure-light">completo</span>.
        </h2>
        <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
          Agende sua avaliação e descubra o melhor tratamento para recuperar sua confiança
          e qualidade de vida.
        </p>
        <a
          href="#contato"
          className="group relative mt-10 inline-flex items-center gap-3 overflow-hidden rounded-full bg-white px-9 py-4 text-sm font-semibold text-navy-deep shadow-[var(--shadow-elegant)] transition-all hover:-translate-y-0.5"
        >
          <span
            className="absolute inset-0 -translate-x-full bg-azure-light transition-transform duration-500 group-hover:translate-x-0"
            aria-hidden
          />
          <span className="relative flex items-center gap-3">
            <MessageCircle className="h-5 w-5" />
            Agendar Avaliação
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </span>
        </a>
      </div>
    </section>
  );
}

/* ───────────────────────── Footer ───────────────────────── */
function Footer() {
  return (
    <footer id="contato" className="bg-navy-deep text-white">
      <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 lg:grid-cols-4 lg:px-10">
        <div className="lg:col-span-1">
          <div className="flex items-center gap-2">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-white font-display text-lg font-bold text-navy-deep">
              LF
            </span>
            <div>
              <div className="font-display text-base font-semibold">Dr. Lucas Ferreira</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">
                Implantodontia
              </div>
            </div>
          </div>
          <p className="mt-5 text-sm leading-relaxed text-white/70">
            Reabilitação oral e implantes dentários com tecnologia, segurança e cuidado humano.
          </p>
          <div className="mt-6 flex gap-3">
            <div
              aria-label="Instagram"
              className="grid h-10 w-10 place-items-center rounded-full border border-white/15 transition-colors hover:bg-azure hover:border-azure"
            >
              <Instagram className="h-4 w-4" />
            </div>
            <div
              aria-label="Facebook"
              className="grid h-10 w-10 place-items-center rounded-full border border-white/15 transition-colors hover:bg-azure hover:border-azure"
            >
              <Facebook className="h-4 w-4" />
            </div>
            <div
              aria-label="WhatsApp"
              className="grid h-10 w-10 place-items-center rounded-full border border-white/15 transition-colors hover:bg-azure hover:border-azure"
            >
              <MessageCircle className="h-4 w-4" />
            </div>
          </div>
        </div>

        <div>
          <div className="mb-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-azure-light">
            Atendimento
          </div>
          <ul className="space-y-3 text-sm text-white/75">
            <li className="flex items-start gap-3">
              <Clock className="mt-0.5 h-4 w-4 text-azure-light" />
              <div>
                Seg – Sex · 09h às 19h
                <br />
                Sáb · 09h às 13h
              </div>
            </li>
          </ul>
        </div>

        <div>
          <div className="mb-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-azure-light">
            Contato
          </div>
          <ul className="space-y-3 text-sm text-white/75">
            <li className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-4 w-4 text-azure-light" />
              Av. Paulista, 1000 — Sala 1201
              <br />
              São Paulo · SP
            </li>
            <li className="flex items-center gap-3">
              <Phone className="h-4 w-4 text-azure-light" />
              (11) 99999-9999
            </li>
          </ul>
        </div>

        <div>
          <div className="mb-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-azure-light">
            Links rápidos
          </div>
          <ul className="space-y-2.5 text-sm text-white/75">
            {[
              ["Início", "#inicio"],
              ["Sobre", "#sobre"],
              ["Tratamentos", "#tratamentos"],
              ["Resultados", "#resultados"],
              ["Depoimentos", "#depoimentos"],
            ].map(([l, h]) => (
              <li key={l}>
                <a href={h} className="transition-colors hover:text-white">
                  {l}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-6 py-6 text-xs text-white/55 sm:flex-row lg:px-10">
          <div>© {new Date().getFullYear()} Dr. Lucas Ferreira · CRO 00000. Todos os direitos reservados.</div>
          <div>Implantodontia · Reabilitação Oral</div>
        </div>
      </div>
    </footer>
  );
}

/* ───────────────────────── Floating CTA ───────────────────────── */
function ContactFab() {
  return (
    <a
      href="#contato"
      aria-label="Ir para contato"
      className="fixed bottom-6 right-6 z-40 grid h-14 w-14 place-items-center rounded-full bg-azure text-white shadow-[var(--shadow-elegant)] transition-transform hover:scale-110"
    >
      <MessageCircle className="h-6 w-6" />
      <span className="absolute inset-0 -z-10 animate-ping rounded-full bg-azure opacity-30" />
    </a>
  );
}

/* ───────────────────────── Page ───────────────────────── */
export default function App() {
  return (
    <main className="min-h-screen bg-background text-foreground">
      <Header />
      <Hero />
      <Problems />
      <About />
      <Treatments />
      <Results />
      <Process />
      <Testimonials />
      <FAQ />
      <FinalCTA />
      <Footer />
      <ContactFab />
    </main>
  );
}
